<script setup lang="ts">
import {event} from '../../analytics.ts';
import {SvgIcon} from '../../svg.ts';
import DiffCommitSelector from '../../components/DiffCommitSelector/DiffCommitSelector.vue';
import {inject, computed, onMounted, onUnmounted, onUpdated, ref} from 'vue';
import {FILE_DIFF_STATUS} from '../types/file_tree.ts';
import {allspiceCookies, COOKIE_NAMES} from '../../allspice-cookies.ts';
import VariantDropdown from '../../components/VariantDropdown.vue';
import type {Store} from '../store.ts';

const emit = defineEmits(['warnings']);

type Variant = {
  id: string;
  name: string;
};

const sidebarVisible = defineModel('sidebarVisible', {
  type: Boolean,
  required: true,
});

defineModel('searchQuery', {
  type: String,
  default: '',
});

defineProps({
  sidebarWidth: {
    type: Number,
    default: 300,
  },
});

const store = inject<Store>('store');

const activeReview = ref(false);

// Whether the commit filter is available (only on pull request pages)
const hasCommitFilter = ref(false);

// Settings dropdown
const settingsOpen = ref(false);
const settingsRef = ref(null);

function toggleSettings() {
  settingsOpen.value = !settingsOpen.value;
}

function onClickOutside(e: MouseEvent) {
  if (settingsRef.value && !settingsRef.value.contains(e.target)) {
    settingsOpen.value = false;
  }
}

function switchToBasicView() {
  event('project_view_disabled');
  allspiceCookies.set(COOKIE_NAMES.PROJECT_VIEW, 'false');
  window.location.reload();
}

// liftend variant selector state
const variantSelection = ref<Variant>({
  id: 'default',
  name: '[No Variations]',
});

function updateVariantSelection(variant: Variant) {
  variantSelection.value = variant;
}

const progress = computed(() => {
  let totalCount = 0;
  let viewedCount = 0;

  for (const path of store.hashToPathMap.value.values()) {
    if (path.status && path.status !== FILE_DIFF_STATUS.UNCHANGED) {
      totalCount++;
      if (path.isViewed) {
        viewedCount++;
      }
    }
  }

  return {
    value: viewedCount,
    max: totalCount,
  };
});

const variantElementAttributes = computed(() => {
  const preview = store.activePreview.value;
  if ('variantHtml' in preview && preview.variantHtml && preview.variantHtml instanceof HTMLElement) {
    return preview.variantHtml.attributes;
  }
  return undefined;
});

onUpdated(() => {
  // clear warnings if we don't have variants
  if (!variantElementAttributes.value) {
    emit('warnings', []);
  }
});

onMounted(() => {
  document.addEventListener('click', onClickOutside);

  // Check if the commit filter data element exists (only on pull request pages)
  hasCommitFilter.value = Boolean(document.querySelector('#diff-commit-select'));

  // get the review button from the hidden div
  const reviewBtn = document.querySelector('#review-box');
  if (!reviewBtn) {
    return;
  }

  // This is a bit of a hack to get the review button to show up in the correct place.
  const reviewBoxPlaceholder = document.querySelector('#review-box-placeholder');
  if (!reviewBoxPlaceholder) {
    console.error('Review box placeholder not found');
    return;
  }

  // replace the placeholder with the correct review button
  reviewBoxPlaceholder.replaceWith(reviewBtn);
  activeReview.value = true;
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <div class="diff-detail-box">
    <div
      class="tw:flex tw:items-center tw:gap-1 tw:shrink-0"
    >
      <button
        class="diff-toggle-file-tree-button not-mobile btn interact-fg tw:shrink-0"
        @click="sidebarVisible = !sidebarVisible"
      >
        <SvgIcon
          :name="sidebarVisible ? 'octicon-sidebar-collapse' : 'octicon-sidebar-expand'"
          :size="20"
          class="icon"
        />
      </button>

      <DiffCommitSelector
        v-if="hasCommitFilter"
        class="tw:ml-2"
      />

      <div
        v-if="sidebarVisible"
        ref="settingsRef"
        class="tw:relative"
      >
        <button
          id="sidebar-settings-button"
          class="btn interact-fg tw:p-1"
          @click="toggleSettings"
        >
          <SvgIcon
            name="octicon-gear"
            :size="18"
          />
        </button>
        <div
          v-show="settingsOpen"
          class="tw:absolute tw:left-0 tw:top-full tw:mt-1 tw:bg-body tw:border tw:border-secondary tw:rounded tw:shadow-lg tw:z-50 tw:min-w-[200px]"
        >
          <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
            <div
              id="enable-basic-view"
              class="tw:flex-1 tw:p-3 tw:cursor-pointer tw:hover:bg-hover"
              @click="switchToBasicView"
            >
              Switch back to Basic View
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="tw:flex tw:align-items tw:gap-1 tw:justify-end">
      <div
        :class="{'tw:invisible': !activeReview}"
        class="not-mobile tw:flex tw:items-center tw:flex-col tw:whitespace-nowrap tw:mr-1"
      >
        <label for="allspice-viewed-files-summary">
          {{ progress.value }} / {{ progress.max }} files viewed
        </label>
        <progress
          id="allspice-viewed-files-summary"
          class="tw:w-full tw:h-[8px]"
          :value="progress.value"
          :max="progress.max"
        />
      </div>
      <VariantDropdown
        v-if="variantElementAttributes"
        :key="store.activePath.value"
        :head-repo-owner="variantElementAttributes.getNamedItem('head-repo-owner')?.value"
        :head-repo-name="variantElementAttributes.getNamedItem('head-repo-name')?.value"
        :base-commit="variantElementAttributes.getNamedItem('base-commit')?.value"
        :head-commit="variantElementAttributes.getNamedItem('head-commit')?.value"
        :head-tree-path="variantElementAttributes.getNamedItem('head-tree-path')?.value"
        :find-warning-component="false"
        :initial-selection="variantSelection"
        @select="updateVariantSelection"
        @warnings="$emit('warnings', $event)"
      />

      <div id="review-box-placeholder" />
    </div>
  </div>
</template>
